/**
 * WorkingEncomienda — panel derecho de Producción: la encomienda en ensamblaje.
 *
 * Decisiones de diseño:
 * - ORDEN BLOQUEADO (principio 17): los paquetes SIEMPRE se renderizan según la
 *   regla del Anexo (generateAnexoPDF.js): entero final del unique_package_id
 *   ascendente, empates por orden alfabético del ID. Sin drag-and-drop; mover
 *   un artículo o cambiar un ID reordena solo porque la regla lo dicta.
 * - Este componente es el único dueño de las mutaciones estructurales
 *   (mover artículo, combinar paquetes, agregar paquete); todas suben como un
 *   array nuevo vía onChangeDraft — Produccion decide autosave.
 * - "Agregar paquete" sugiere el siguiente ID con el prefijo dominante de la
 *   encomienda (p. ej. AV-7 si existen AV-5 y AV-6) — menos tecleo, la regla
 *   de orden queda satisfecha sola.
 * - El mapa mensaje→timestamp se calcula aquí (el draft completo vive aquí)
 *   y baja a las filas para el indicador de procedencia (principio 16).
 */
import { PackagePlus } from 'lucide-react';
import HeaderStrip from './HeaderStrip';
import PackageBlock from './PackageBlock';

// Regla de orden del Anexo — BLOQUEADA (ver generateAnexoPDF.js).
export const ordenarPaquetesAnexo = (paquetes) =>
  [...(paquetes || [])].sort((a, b) => {
    const num = (p) => {
      const m = String(p.unique_package_id || '').match(/(\d+)\s*$/);
      return m ? parseInt(m[1], 10) : 0;
    };
    const d = num(a) - num(b);
    if (d !== 0) return d;
    return String(a.unique_package_id || '').localeCompare(String(b.unique_package_id || ''));
  });

const fechaHoy = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const siguienteIdPaquete = (paquetes) => {
  const prefijos = {};
  let mayor = 0;
  for (const p of paquetes) {
    const m = String(p.unique_package_id || '').match(/^(.+)-(\d+)\s*$/);
    if (m) {
      prefijos[m[1]] = (prefijos[m[1]] || 0) + 1;
      mayor = Math.max(mayor, parseInt(m[2], 10));
    }
  }
  const dominante =
    Object.keys(prefijos).sort((a, b) => prefijos[b] - prefijos[a])[0] || 'PK';
  return `${dominante}-${mayor + 1}`;
};

const WorkingEncomienda = ({
  draft,
  onChangeDraft,
  quoteBuffer,
  onConsumeQuote,
  onJumpToMessage,
  onAskClient,
}) => {
  const paquetes = ordenarPaquetesAnexo(draft.packages);
  const customsConfig = draft.customs_config_snapshot;

  const timestampsPorMensaje = {};
  for (const msg of draft.whatsapp_thread || []) {
    timestampsPorMensaje[msg.id] = msg.timestamp;
  }

  const reemplazarPaquete = (idOriginal, pkgNuevo) => {
    onChangeDraft(
      draft.packages.map((p) => (p.unique_package_id === idOriginal ? pkgNuevo : p))
    );
  };

  const moverItem = (itemId, idOrigen, idDestino) => {
    const origen = draft.packages.find((p) => p.unique_package_id === idOrigen);
    if (!origen) return;
    const item = (origen.items || []).find((it) => it.id === itemId);
    if (!item) return;

    let siguientes = draft.packages.map((p) =>
      p.unique_package_id === idOrigen
        ? { ...p, items: (p.items || []).filter((it) => it.id !== itemId) }
        : p
    );

    if (idDestino === null) {
      // Paquete nuevo con el artículo movido
      siguientes = [
        ...siguientes,
        {
          unique_package_id: siguienteIdPaquete(draft.packages),
          recipient_name: '',
          taxes: { dai: 0 },
          items: [item],
        },
      ];
    } else {
      siguientes = siguientes.map((p) =>
        p.unique_package_id === idDestino ? { ...p, items: [...(p.items || []), item] } : p
      );
    }
    onChangeDraft(siguientes);
  };

  const combinarPaquetes = (idOrigen, idDestino) => {
    const origen = draft.packages.find((p) => p.unique_package_id === idOrigen);
    if (!origen || idOrigen === idDestino) return;
    const siguientes = draft.packages
      .filter((p) => p.unique_package_id !== idOrigen)
      .map((p) =>
        p.unique_package_id === idDestino
          ? { ...p, items: [...(p.items || []), ...(origen.items || [])] }
          : p
      );
    onChangeDraft(siguientes);
  };

  const agregarPaquete = () => {
    onChangeDraft([
      ...draft.packages,
      {
        unique_package_id: siguienteIdPaquete(draft.packages),
        recipient_name: '',
        taxes: { dai: 0 },
        items: [],
      },
    ]);
  };

  return (
    <div className="flex flex-col gap-3">
      <HeaderStrip snapshot={draft.customs_config_snapshot} fecha={fechaHoy()} />

      {paquetes.length === 0 && (
        <p
          className="rounded-lg border px-4 py-6 text-center text-sm"
          style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)', background: 'var(--tile)' }}
        >
          El cliente todavía no declaró destinatarios. Puede esperar su mensaje o
          agregar el primer paquete manualmente.
        </p>
      )}

      {paquetes.map((pkg) => (
        <PackageBlock
          key={pkg.unique_package_id}
          pkg={pkg}
          allPackages={paquetes}
          customsConfig={customsConfig}
          onChange={(pkgNuevo) => reemplazarPaquete(pkg.unique_package_id, pkgNuevo)}
          onMoveItem={moverItem}
          onMerge={combinarPaquetes}
          quoteBuffer={quoteBuffer}
          onConsumeQuote={onConsumeQuote}
          onJumpToMessage={onJumpToMessage}
          onAskClient={onAskClient}
          timestampsPorMensaje={timestampsPorMensaje}
        />
      ))}

      <button
        type="button"
        onClick={agregarPaquete}
        aria-label="Agregar un paquete nuevo a la encomienda"
        className="inline-flex min-h-[44px] items-center justify-center gap-1.5 self-start rounded-lg border border-dashed px-4 text-sm font-medium
                   hover:bg-[var(--nav-active-bg)]
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
        style={{ borderColor: 'var(--border-hover)', color: 'var(--text-secondary)' }}
      >
        <PackagePlus size={15} aria-hidden="true" /> Agregar paquete
      </button>
    </div>
  );
};

export default WorkingEncomienda;

/*
Ejemplo de uso (en Produccion):
  <WorkingEncomienda
    draft={draft}
    onChangeDraft={(paquetesNuevos) => aplicarCambios(paquetesNuevos)}
    quoteBuffer={quoteBuffer}
    onConsumeQuote={() => setQuoteBuffer(null)}
    onJumpToMessage={(msgId) => setJumpToMessageId(msgId)}
    onAskClient={enviarPreguntaCampo}
  />
*/
